import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpdateCalculatedCommissionList } from "../../../../modules/commissions/batch/types";
import { splitCommissionsApi } from "../../../../modules/commissions/splitcommissions/api";
import type {
  SplitCommissionsExportRequest,
  SplitCommissionsFilterPageRequest,
  UpdateSplitCommission,
} from "../../../../modules/commissions/splitcommissions/types";
import { EntityObject } from "../../../types";
import { COMMISSIONS_BATCH_QUERY_KEYS, SPLIT_COMMISSIONS_QUERY_KEYS } from "./queryKeys";

export const useFilterSplitCommissions = (payload: SplitCommissionsFilterPageRequest) => {
  return useQuery({
    queryKey: SPLIT_COMMISSIONS_QUERY_KEYS.filterSplitCommissions(payload),
    queryFn: async () => {
      const { data } = await splitCommissionsApi.filterSplitCommissions(payload);

      return data;
    },
    staleTime: 0, // TODO remove this when we have all batch commissions actions in react query and we will be able to invalidate this query in specific situations
    placeholderData: keepPreviousData,
  });
};

export const useDownloadSplitCommissionsExport = () => {
  return useMutation({
    mutationKey: SPLIT_COMMISSIONS_QUERY_KEYS.downloadSplitCommissionsExport(),
    mutationFn: async (payload: SplitCommissionsExportRequest) => {
      const response = await splitCommissionsApi.downloadSplitCommissionsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useUpdateSplitCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SPLIT_COMMISSIONS_QUERY_KEYS.updateSplitCommissions(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateSplitCommission>) => {
      const { data } = await splitCommissionsApi.updateSplitCommissions(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPLIT_COMMISSIONS_QUERY_KEYS.filterSplitCommissions() });
    },
  });
};

export const useUpdateSplitCalculatedCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SPLIT_COMMISSIONS_QUERY_KEYS.updateSplitCalculatedCommissions(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateCalculatedCommissionList>) => {
      const { data } = await splitCommissionsApi.updateSplitCalculatedCommissions(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPLIT_COMMISSIONS_QUERY_KEYS.filterSplitCommissions() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissions() });
    },
  });
};
